import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';
import { useTenant } from '../../context/TenantContext';
import { useTranslation } from 'react-i18next';
import { useFormatters } from '../../utils/format';

/**
 * TenantSwitcher
 * ----------------------------------------------------------------------------
 * Dropdown that lets the presenter switch between the five early-validation
 * logo scenarios mid-demo. Each scenario is a narratively distinct story,
 * so the switcher surfaces the tagline along with the company name.
 */

const FlagEmoji = ({ code }) => {
  const map = { DE: '🇩🇪', ES: '🇪🇸', CH: '🇨🇭', GB: '🇬🇧', FR: '🇫🇷' };
  return <span aria-hidden className="text-base leading-none">{map[code] || '🏢'}</span>;
};

const narrativeBadge = (role, t) => {
  const map = {
    preventive:    { labelKey: 'tenant.narrativeRoles.preventive',    className: 'bg-success/10 text-success border-success/20' },
    crisis:        { labelKey: 'tenant.narrativeRoles.crisis',        className: 'bg-destructive/10 text-destructive border-destructive/20' },
    scale:         { labelKey: 'tenant.narrativeRoles.scale',         className: 'bg-primary/10 text-primary-solid border-primary/20' },
    'ai-insight':  { labelKey: 'tenant.narrativeRoles.ai-insight',    className: 'bg-warning/10 text-warning border-warning/20' },
    international: { labelKey: 'tenant.narrativeRoles.international', className: 'bg-slate-100 text-slate-700 border-slate-200' },
  };
  const cfg = map[role] || map.preventive;
  return { label: t(cfg.labelKey), className: cfg.className };
};

const TenantSwitcher = () => {
  const { tenant, tenants, switchTenant } = useTenant();
  const { t } = useTranslation();
  const { num } = useFormatters();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const activeBadge = narrativeBadge(tenant.narrativeRole, t);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="group flex items-center gap-2 pl-2 pr-2.5 py-1.5 rounded-md border border-border bg-card hover:bg-muted transition-smooth"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <FlagEmoji code={tenant.countryCode} />
        <div className="flex flex-col items-start min-w-0">
          <span className="text-xs font-medium text-foreground truncate max-w-[140px] sm:max-w-[200px]">
            {tenant.name}
          </span>
          <span className="text-[10px] text-muted-foreground truncate max-w-[140px] sm:max-w-[200px]">
            {t('tenant.employees', { count: num(tenant.employees) })}
          </span>
        </div>
        <Icon name="ChevronDown" size={14} className={`text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute left-0 top-full mt-2 w-[360px] max-w-[calc(100vw-2rem)] bg-popover border border-border rounded-lg shadow-elevated z-50 overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-border bg-muted/50">
            <div className="flex items-center gap-2">
              <Icon name="Building2" size={14} className="text-muted-foreground" />
              <span className="text-xs font-semibold text-foreground uppercase tracking-wide">
                {t('tenant.switcherLabel')}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {t('tenant.switcherSubtitle')}
            </p>
          </div>

          <div className="max-h-[460px] overflow-y-auto">
            {tenants.map((entry) => {
              const badge = narrativeBadge(entry.narrativeRole, t);
              const active = entry.id === tenant.id;
              return (
                <button
                  key={entry.id}
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => { switchTenant(entry.id); setOpen(false); }}
                  className={`w-full text-left px-4 py-3 border-b border-border last:border-b-0 transition-smooth ${
                    active ? 'bg-primary/5' : 'hover:bg-muted'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="pt-0.5">
                      <FlagEmoji code={entry.countryCode} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-semibold text-foreground truncate">
                          {entry.name}
                        </span>
                        {active && (
                          <Icon name="Check" size={14} className="text-primary-solid shrink-0" />
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mb-1.5">
                        {entry.industry} · {entry.country} · {t('tenant.employees', { count: num(entry.employees) })}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${badge.className}`}>
                          {badge.label}
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          {t('tenant.meanGapShort')}: <span className="font-semibold text-foreground">{entry.metrics.meanPayGap}%</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="px-4 py-2.5 border-t border-border bg-muted/40">
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Icon name="Info" size={12} />
              <span>{t('tenant.footerHint')}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantSwitcher;
